import React, { useState } from "react";
import { useSavedQueries } from "./useSavedQueries";

/**
 * Save-and-reuse panel: names and saves the current builder config, and lists
 * the company's saved queries with load/delete. Saving is disabled until there
 * is a valid config (a module with selected fields).
 * @param {object} props
 * @param {() => (object|null)} props.getConfig returns the current query config.
 * @param {(config: object) => void} props.onLoad loads a saved config into the builder.
 * @param {boolean} props.canSave whether the current builder state is saveable.
 * @param {string[]} props.knownDatasets dataset keys that can still be loaded.
 * @returns {JSX.Element}
 */
export default function SavedQueriesPanel({
  getConfig,
  onLoad,
  canSave,
  knownDatasets = [],
}) {
  const { items, loading, error, save, remove } = useSavedQueries();
  const [name, setName] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSave = async () => {
    const config = getConfig();
    if (!config || !name.trim()) return;
    setBusy(true);
    const res = await save(name.trim(), config);
    setBusy(false);
    setFeedback(res.ok ? { ok: true, text: "Saved." } : { ok: false, text: res.message || "Save failed" });
    if (res.ok) setName("");
  };

  /**
   * Deletes a saved query, surfacing the reason when the server refuses — most
   * often because a reminder rule still depends on it.
   * @param {number} id the saved-query id.
   */
  const onDelete = async (id) => {
    const res = await remove(id);
    setFeedback(
      res.ok
        ? { ok: true, text: "Deleted." }
        : { ok: false, text: res.message || "Delete failed" },
    );
  };

  return (
    <div className="qb-saved">
      <h3 className="qb-subtitle">Saved queries</h3>

      <div className="qb-saved-form">
        <input
          className="qb-input"
          aria-label="Query name"
          placeholder="Name this query…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="button"
          className="qb-btn qb-btn-primary"
          onClick={onSave}
          disabled={!canSave || !name.trim() || busy}
        >
          {busy ? "Saving…" : "Save query"}
        </button>
      </div>

      {feedback && (
        <p className={feedback.ok ? "qb-muted" : "qb-error"} role="status">
          {feedback.text}
        </p>
      )}
      {error && (
        <div className="qb-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="qb-muted">Loading saved queries…</p>
      ) : items.length === 0 ? (
        <p className="qb-muted">No saved queries yet.</p>
      ) : (
        <ul className="qb-saved-list">
          {items.map((q) => {
            const loadable = knownDatasets.includes(q.moduleKey);
            return (
            <li key={q.id} className="qb-saved-item">
              <span className="qb-saved-name">
                {q.name}
                {!loadable && (
                  <span className="qb-saved-tag"> · module unavailable</span>
                )}
              </span>
              <span className="qb-saved-actions">
                <button
                  type="button"
                  className="qb-btn qb-btn-ghost"
                  onClick={() => onLoad(q.config)}
                  disabled={!loadable}
                  title={loadable ? "" : `Module "${q.moduleKey}" is no longer available`}
                >
                  Load
                </button>
                <button
                  type="button"
                  className="qb-icon-btn"
                  aria-label={`Delete ${q.name}`}
                  onClick={() => onDelete(q.id)}
                >
                  ×
                </button>
              </span>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
